import type { BusConfig, CargoConfig } from '../config/bus.config';
import type { VehicleSeat } from '../player/VehicleSeat';
import { clamp01 } from '../utils/math';

/** How the van touched down after a ramp: straight, slanted, very slanted (on two wheels). */
export const BusLanding = {
  Clean: 'CLEAN',
  Slanted: 'SLANTED',
  Rough: 'ROUGH',
} as const;
export type BusLanding = (typeof BusLanding)[keyof typeof BusLanding];

/** Why load left the roof. */
export const CargoLoss = {
  /** A crash with another vehicle (the rival from the side, oncoming traffic head-on). */
  Crash: 'CRASH',
  /** Shaken off by a slanted or very slanted ramp landing. */
  Landing: 'LANDING',
} as const;
export type CargoLoss = (typeof CargoLoss)[keyof typeof CargoLoss];

/** Forward speed factor after `driven` m of the ride: linear from start to end over the ride ("immer schneller"). */
export function busSpeedFactor(config: Pick<BusConfig, 'speedFactor' | 'rideLength'>, driven: number): number {
  const { start, end } = config.speedFactor;
  return start + (end - start) * clamp01(driven / config.rideLength);
}

/** Landing quality from the lateral speed at touch-down: steering while landing tips the van onto two wheels. */
export function landingQuality(config: Pick<BusConfig, 'landing'>, lateralSpeed: number): BusLanding {
  const speed = Math.abs(lateralSpeed);
  if (speed < config.landing.cleanBelow) return BusLanding.Clean;
  return speed < config.landing.slantedBelow ? BusLanding.Slanted : BusLanding.Rough;
}

/**
 * Pieces of load a crash costs (user 2026-10-05): head-on more than from the side, and most when the van is rammed in the
 * air after a ramp – never more than there are.
 */
export function crashLoss(config: CargoConfig, crash: { readonly headOn: boolean; readonly airborne: boolean }, cargo: number): number {
  const pieces = crash.airborne ? config.loss.airborne : crash.headOn ? config.loss.headOn : config.loss.side;
  return Math.min(cargo, pieces);
}

/** Pieces of load a ramp landing costs: a clean one nothing, steering while touching down some – never more than there are. */
export function landingLoss(config: CargoConfig, quality: BusLanding, cargo: number): number {
  if (quality === BusLanding.Clean) return 0;
  return Math.min(cargo, quality === BusLanding.Slanted ? config.landing.slanted : config.landing.rough);
}

/**
 * The van as a seat of the PlayerController (DRIVING): its body is the collider, it steers with the lane logic at its
 * own pace and its speed factor rises with the meters driven – the BusManager sets `driven`.
 */
export class BusSeat implements VehicleSeat {
  /** Meters driven in this ride (BusManager). */
  driven = 0;
  readonly seatHeight: number;
  readonly laneChangeDuration: number;
  readonly body: VehicleSeat['body'];
  readonly throwOff: VehicleSeat['throwOff'];
  /** The jump key does nothing in the van – the ride is a fixed stretch (no accidental bail-out). */
  readonly jumpExits = false;

  constructor(private readonly config: BusConfig) {
    this.seatHeight = config.seat.y;
    this.laneChangeDuration = config.laneChangeDuration;
    this.body = config.body;
    this.throwOff = { ahead: config.throwOff.ahead, launchSpeed: config.throwOff.speed };
  }

  get speedFactor(): number {
    return busSpeedFactor(this.config, this.driven);
  }
}
